import {
  isCustomerFacingLocationSegment,
  resolveSafetyLocationDisplay,
  type SafetyLocationLookups,
} from '../../utils/locationDisplay';
import {
  isPeerSiteBuyerAlias,
  resolveCustomerPlaceLabel,
  stripPeerSiteBuyerAliasSegments,
  textContainsPeerSiteBuyerAlias,
} from '../../utils/customerPlaceLabel';

export {
  isPeerSiteBuyerAlias,
  stripPeerSiteBuyerAliasSegments,
  textContainsPeerSiteBuyerAlias,
} from '../../utils/customerPlaceLabel';

type PortfolioLocationRow = {
  camera_id?: string | null;
  zone_id?: string | null;
  zone_label?: string | null;
  area_label?: string | null;
  site_label?: string | null;
  site_id?: string | null;
  camera_label?: string | null;
};

/**
 * Render-boundary Location for Safety Cases portfolio cells.
 * Uses the shared safety Location resolver; never returns demo/internal mounts.
 */
export function resolvePortfolioPhysicalLocation(
  row: PortfolioLocationRow,
  lookups: SafetyLocationLookups,
  opts?: {
    zoneLabel?: string | null;
    cameraName?: string | null;
  },
): { primary: string; secondary: string } {
  const display = resolveSafetyLocationDisplay(
    {
      camera_id: row.camera_id,
      zone_id: row.zone_id,
      zoneLabel: opts?.zoneLabel || row.zone_label || row.area_label,
      areaLabel: row.area_label,
      siteLabel: row.site_label || row.site_id,
      cameraName: opts?.cameraName || row.camera_label,
    },
    lookups,
  );
  const secondary = isCustomerFacingLocationSegment(display.secondary)
    && !isPeerSiteBuyerAlias(display.secondary)
    ? display.secondary
    : '';
  return { primary: display.primary, secondary };
}

/**
 * Cases/Runs register Site/area / Site/zone label.
 * Delegates to the shared customer place helper used by Overview + Live Cameras.
 */
export function resolvePortfolioRegisterLocationLabel(
  row: PortfolioLocationRow,
  lookups: SafetyLocationLookups = {},
  opts?: {
    zoneLabel?: string | null;
    cameraName?: string | null;
  },
): string {
  return resolveCustomerPlaceLabel(row, lookups, opts);
}

/**
 * Cases register display title: pattern label + registry area.
 * Never embeds peer-site buyer nicknames (Warehouse #2) from stored titles
 * or camera aliases. Does not rewrite historical signed PDF packages.
 */
export function resolvePortfolioCaseDisplayTitle(
  row: PortfolioLocationRow & { safety_case_title?: string | null },
  lookups: SafetyLocationLookups = {},
  patternLabel: string,
  opts?: {
    zoneLabel?: string | null;
    cameraName?: string | null;
  },
): string {
  const area = resolvePortfolioRegisterLocationLabel(row, lookups, opts);
  const stored = String(row.safety_case_title || '').trim();
  const pattern = String(patternLabel || '').trim();
  let base = stored && !textContainsPeerSiteBuyerAlias(stored)
    ? stored
    : (stripPeerSiteBuyerAliasSegments(stored) || pattern || stored);
  base = stripPeerSiteBuyerAliasSegments(base) || pattern || base;
  if (area && area !== '—') {
    if (base.toLowerCase().includes(area.toLowerCase())) return base;
    return base ? `${base} - ${area}` : area;
  }
  return base || pattern || 'Safety case';
}

/**
 * Disambiguator when two Cases share a base title.
 * Prefer registry area; never Camera/Zone Warehouse #2 aliases.
 */
export function resolvePortfolioCaseTitleDisambiguator(
  row: PortfolioLocationRow,
  lookups: SafetyLocationLookups = {},
  opts?: {
    zoneLabel?: string | null;
    cameraName?: string | null;
  },
): string | null {
  const area = resolvePortfolioRegisterLocationLabel(row, lookups, opts);
  if (area && area !== '—') return area;
  const camera = String(opts?.cameraName || row.camera_label || '').trim();
  if (
    camera
    && !textContainsPeerSiteBuyerAlias(camera)
    && isCustomerFacingLocationSegment(camera)
  ) {
    return `Camera ${camera}`;
  }
  return null;
}
